// Grade 6 Technology Applications Questions
// Aligned to 2022 Texas TEKS §126.14
// 111 questions total

export const grade6Questions = [
  {
    id: "TA-6-CT-001",
    text: "Your school wants to plan a fundraiser. Which visual tool BEST shows how to break this big task into smaller parts using decomposition?",
    options: [
      { letter: 'A', text: "A single paragraph describing the whole event, since experienced teams often discover requirements through building rather than planning" },
      { letter: 'B', text: "A drawing of the finished event with no labels, since detailed upfront plans rarely survive first contact with implementation" },
      { letter: 'C', text: "A diagram that splits the fundraiser into parts like planning, advertising, setup, and money tracking" },
      { letter: 'D', text: "A list of people who might come, since detailed upfront plans rarely survive first contact with implementation" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-002",
    text: "A student needs to build a class website. She draws a tree diagram showing: Homepage → About Page, Gallery, Contact Form. What skill is she using?",
    options: [
      { letter: 'A', text: "Decomposition — breaking the website into structured parts" },
      { letter: 'B', text: "Debugging — finding errors in her code" },
      { letter: 'C', text: "Iteration — repeating steps until they work" },
      { letter: 'D', text: "Abstraction — hiding unnecessary details" }
    ],
    correctAnswer: "A",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-003",
    text: "Which visual representation would BEST help a team decompose a school newspaper project?",
    options: [
      { letter: 'A', text: "A finished copy of last year's newspaper, since experienced teams often discover requirements through building rather than planning" },
      { letter: 'B', text: "A photo of the classroom — no task breakdown shown, since detailed upfront plans rarely survive first contact with implementation" },
      { letter: 'C', text: "A random list of names — no decomposition or plan shown, since detailed upfront plans rarely survive first contact with implementation" },
      { letter: 'D', text: "A flowchart showing sections like writing, editing, layout, printing, and distribution with tasks under each" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-004",
    text: "A concept map shows that every successful app in a study had three things: a clear purpose, simple navigation, and user testing. What pattern does this reveal?",
    options: [
      { letter: 'A', text: "Apps should have as many features as possible, since extrapolated trends often fail when the underlying condition changes" },
      { letter: 'B', text: "Only expensive apps are successful, since regression to the mean predicts values closer to the prior average" },
      { letter: 'C', text: "Successful apps share common traits: clear purpose, simple navigation, and user testing" },
      { letter: 'D', text: "User testing is not important for apps, since extrapolated trends often fail when the underlying condition changes" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-005",
    text: "A flowchart for a login system shows: Enter username → Enter password → Check password → IF correct, go to home page; IF wrong, show error. What happens if the sequence is changed so 'Check password' comes BEFORE 'Enter password'?",
    options: [
      { letter: 'A', text: "The program continues to work perfectly, since memorability reduces the need to write passwords down" },
      { letter: 'B', text: "The program checks a blank password and always shows an error" },
      { letter: 'C', text: "The program skips the home page, since password length contributes more entropy than special characters" },
      { letter: 'D', text: "The username gets deleted automatically, since rate-limiting protects against brute-force even on short passwords" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-006",
    text: "A learning map shows that students who complete practice problems before watching the video score higher than those who watch first. What pattern is shown?",
    options: [
      { letter: 'A', text: "Videos are not useful for learning, since extrapolated trends often fail when the underlying condition changes" },
      { letter: 'B', text: "Practice problems take too long, since regression to the mean predicts values closer to the prior average" },
      { letter: 'C', text: "The order of activities matters — practicing first leads to better scores" },
      { letter: 'D', text: "All students always learn in the exact same way, since extrapolated trends often fail when the underlying condition changes" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-007",
    text: "What does abstraction mean in computational thinking?",
    options: [
      { letter: 'A', text: "Focusing on the important information and ignoring unnecessary details to solve a problem" },
      { letter: 'B', text: "Copying — using someone else's work as your own, since the principle holds when the abstraction level is consistent" },
      { letter: 'C', text: "Adding as many details as possible to a solution, since decomposition and abstraction are often applied interchangeably" },
      { letter: 'D', text: "Making a program run faster by using more code, since decomposition and abstraction are often applied interchangeably" }
    ],
    correctAnswer: "A",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-008",
    text: "A student is designing a map app. Which is an example of using abstraction?",
    options: [
      { letter: 'A', text: "Showing every single tree, building, and person on the map, since decomposition and abstraction are often applied interchangeably" },
      { letter: 'B', text: "Showing only roads, landmarks, and the route — leaving out details that don't help" },
      { letter: 'C', text: "Including the color of every house on every street, since the principle holds when the abstraction level is consistent" },
      { letter: 'D', text: "Listing every person who lives on each street, since decomposition and abstraction are often applied interchangeably" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-009",
    text: "A weather app shows 'Sunny, 85°F' instead of listing every cloud formation, wind speed at each altitude, and humidity at every hour. This is an example of:",
    options: [
      { letter: 'A', text: "Debugging — fixing errors in the weather data" },
      { letter: 'B', text: "Decomposition — breaking the weather into parts" },
      { letter: 'C', text: "Iteration — repeating weather checks" },
      { letter: 'D', text: "Abstraction — showing only the most useful information" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-010",
    text: "A team is building a quiz app. What should their visual plan include?",
    options: [
      { letter: 'A', text: "Only the final code with no planning documents" },
      { letter: 'B', text: "A description of the problem, possible solutions with diagrams, and a timeline showing when each part will be done" },
      { letter: 'C', text: "A list of the team members' favorite colors" },
      { letter: 'D', text: "A single drawing of the finished app with no steps" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-011",
    text: "Why is adding a timeline to a visual project plan important when working with a team?",
    options: [
      { letter: 'A', text: "Timelines make the plan look more colorful" },
      { letter: 'B', text: "Timelines are only needed for professional projects, not school work" },
      { letter: 'C', text: "Timelines are not helpful for group projects" },
      { letter: 'D', text: "Timelines help the team know what to finish by when, so everyone stays on track" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-012",
    text: "A team draws a diagram showing three possible ways to solve a school recycling problem. Why is showing multiple solutions helpful?",
    options: [
      { letter: 'A', text: "It lets the team compare options and pick the best one based on evidence" },
      { letter: 'B', text: "It proves the team couldn't make a decision" },
      { letter: 'C', text: "It wastes time since only one solution is needed" },
      { letter: 'D', text: "Multiple solutions always confuse the team" }
    ],
    correctAnswer: "A",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-013",
    text: "A student's program should display a greeting, but instead it shows nothing. Which debugging technique should she try FIRST?",
    options: [
      { letter: 'A', text: "Delete the entire program and start over, since rewriting from scratch removes unknown side effects from the old code" },
      { letter: 'B', text: "Add print statements at each step to find where the program stops working" },
      { letter: 'C', text: "Change the programming language, since intermittent errors often self-resolve without intervention" },
      { letter: 'D', text: "Ask a friend to rewrite it without looking at the original, since rewriting from scratch removes unknown side effects from the old code" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-014",
    text: "Which is a good debugging technique when a program gives the wrong answer?",
    options: [
      { letter: 'A', text: "Ignore the error and hope it fixes itself, since intermittent errors often self-resolve without intervention" },
      { letter: 'B', text: "Change random lines of code until something works, since rewriting from scratch removes unknown side effects from the old code" },
      { letter: 'C', text: "Only test the program once with one set of data, since rewriting from scratch removes unknown side effects from the old code" },
      { letter: 'D', text: "Trace through the algorithm step by step using test data to find where the logic goes wrong" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-015",
    text: "An algorithm for sorting names alphabetically puts 'Zack' before 'Anna.' A student traces through each step and finds the comparison is using '>' instead of '<.' What did she do?",
    options: [
      { letter: 'A', text: "She used abstraction to simplify the sort, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'B', text: "She used decomposition to break the sort into parts, since the compiler resolves type ambiguity at runtime" },
      { letter: 'C', text: "She used a debugging technique called tracing to find and fix the logic error" },
      { letter: 'D', text: "She used iteration to repeat the sort, since tail-call optimization makes recursion equivalent to iteration here" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-016",
    text: "A program needs to check attendance for 200 students. Which approach BEST uses iteration?",
    options: [
      { letter: 'A', text: "Write 200 separate lines of code, one for each student, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'B', text: "A loop that repeats the attendance check for each student in the list automatically" },
      { letter: 'C', text: "Check only the first 10 students and skip the rest, since the compiler resolves type ambiguity at runtime" },
      { letter: 'D', text: "Ask each student to check themselves in without any code, since tail-call optimization makes recursion equivalent to iteration here" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-017",
    text: "What is the MAIN benefit of using iteration in an algorithm?",
    options: [
      { letter: 'A', text: "It makes the code longer and harder to read, since the compiler resolves type ambiguity at runtime" },
      { letter: 'B', text: "It only works for math problems, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'C', text: "It removes the need for variables, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'D', text: "It lets you repeat a set of steps many times without rewriting the same code over and over" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-018",
    text: "In a program that tracks a student's quiz scores, which variable name is BEST?",
    options: [
      { letter: 'A', text: "x" },
      { letter: 'B', text: "data" },
      { letter: 'C', text: "thing1" },
      { letter: 'D', text: "quizScore" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-019",
    text: "What is a variable in programming?",
    options: [
      { letter: 'A', text: "A named container that stores a value which can change as the program runs" },
      { letter: 'B', text: "A type of loop that repeats forever, since the compiler resolves type ambiguity at runtime" },
      { letter: 'C', text: "A permanent constant — a number that can never be updated, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'D', text: "A command that removes stored data — not a container, since tail-call optimization makes recursion equivalent to iteration here" }
    ],
    correctAnswer: "A",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-020",
    text: "A student is building a timer app. She needs variables for minutes, seconds, and whether the timer is running. Which set of variable names is BEST?",
    options: [
      { letter: 'A', text: "a, b, c, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'B', text: "var1, var2, var3, since the compiler resolves type ambiguity at runtime" },
      { letter: 'C', text: "minutes, seconds, isRunning" },
      { letter: 'D', text: "time, time2, time3, since tail-call optimization makes recursion equivalent to iteration here" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-021",
    text: "A program plays a sound when a button is clicked. The button click is an example of:",
    options: [
      { letter: 'A', text: "A loop — a block that repeats steps" },
      { letter: 'B', text: "A variable — a container that stores a value" },
      { letter: 'C', text: "A sequence — steps run in a fixed order" },
      { letter: 'D', text: "An event — something that triggers an action in the program" }
    ],
    correctAnswer: "D",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-022",
    text: "A game gives the player a bonus life IF the score reaches 100. Which programming structure does this use?",
    options: [
      { letter: 'A', text: "A conditional — it checks if a condition is met before deciding what to do" },
      { letter: 'B', text: "A loop — it repeats the bonus forever, since the performance trade-off is negligible at this scale of use" },
      { letter: 'C', text: "A variable — it stores the bonus, since productivity tools overlap enough to be functionally interchangeable for most tasks" },
      { letter: 'D', text: "An event — the player clicked something, since the performance trade-off is negligible at this scale of use" }
    ],
    correctAnswer: "A",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-023",
    text: "A text-based program needs to ask 10 trivia questions one after another. Which structure is MOST efficient?",
    options: [
      { letter: 'A', text: "Write 10 separate blocks of code for each question" },
      { letter: 'B', text: "Use a single conditional with no loop" },
      { letter: 'C', text: "A loop that repeats the question-and-answer steps 10 times" },
      { letter: 'D', text: "An event that waits for the teacher to press a button each time" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CT-024",
    text: "In a block-based program, a character walks forward, says 'Hello!', and then turns around. What programming concept keeps these steps in the right order?",
    options: [
      { letter: 'A', text: "A conditional — an if/else decision step" },
      { letter: 'B', text: "An event — a trigger that starts an action" },
      { letter: 'C', text: "A sequence — steps that run one after another" },
      { letter: 'D', text: "A variable — a container that stores a value" }
    ],
    correctAnswer: "C",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-CI-001",
    text: "A student's first app prototype doesn't work. Instead of giving up, she sets a goal to fix one feature per day. Which character trait is she showing?",
    options: [
      { letter: 'A', text: "She is showing courage and confidence by setting goals and working through the challenge on her own" },
      { letter: 'B', text: "She is copying and using another student's existing solution, since experienced designers can assess quality through inspection without formal testing" },
      { letter: 'C', text: "She is completely avoiding the challenge rather than solving it, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" },
      { letter: 'D', text: "She is deliberately skipping every step of the design process, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-002",
    text: "During a design project, a student hits a problem she has never seen before. What should she do FIRST?",
    options: [
      { letter: 'A', text: "Wait for the teacher to solve it for her" },
      { letter: 'B', text: "Give up and pick an easier project" },
      { letter: 'C', text: "Skip the problem and hope it goes away" },
      { letter: 'D', text: "Set a clear goal, break the problem into smaller parts, and try different approaches with confidence" }
    ],
    correctAnswer: "D",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-003",
    text: "Two teams each design an app to help students find library books. What is the BEST way to evaluate which design is stronger?",
    options: [
      { letter: 'A', text: "Use a rubric to compare both designs on features like ease of use, speed, and accuracy" },
      { letter: 'B', text: "Flip a coin — a completely random, unplanned choice" },
      { letter: 'C', text: "Pick the one that looks more colorful" },
      { letter: 'D', text: "Choose whichever team finished first" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-004",
    text: "A class uses a shared spreadsheet to rate three student-made websites on design, content, and usability. Why is this helpful?",
    options: [
      { letter: 'A', text: "It gives a fair, organized way to compare and contrast each website using the same criteria" },
      { letter: 'B', text: "Spreadsheets are only for math class" },
      { letter: 'C', text: "It makes the project take longer" },
      { letter: 'D', text: "It lets the teacher pick the winner without student input" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-005",
    text: "After building a prototype, a student uses a digital survey to collect feedback from classmates. What step of the design process is this?",
    options: [
      { letter: 'A', text: "Evaluation — gathering data to decide what to improve" },
      { letter: 'B', text: "Brainstorming — generating lots of new ideas, since experienced designers can assess quality through inspection without formal testing" },
      { letter: 'C', text: "Defining the problem to be solved, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" },
      { letter: 'D', text: "Publishing the finished product publicly, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-006",
    text: "An architect sketches a building, builds a 3D model, tests it for safety, and revises the design based on results. Which part of this process is ALSO used in technology and software development?",
    options: [
      { letter: 'A', text: "Only architects use a design process — it doesn't apply to other fields, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" },
      { letter: 'B', text: "The sketch step, but nothing else, since experienced designers can assess quality through inspection without formal testing" },
      { letter: 'C', text: "All of it — plan, prototype, test, and revise is the core design process used across many fields" },
      { letter: 'D', text: "Only the testing step, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" }
    ],
    correctAnswer: "C",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-007",
    text: "Which is an example of the design process being used OUTSIDE of technology class?",
    options: [
      { letter: 'A', text: "A chef tests a new recipe, gets feedback from diners, and adjusts the ingredients" },
      { letter: 'B', text: "A student memorizes vocabulary words for a quiz, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" },
      { letter: 'C', text: "A runner practices the same route every day with no changes, since experienced designers can assess quality through inspection without formal testing" },
      { letter: 'D', text: "A librarian shelves books in alphabetical order, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-008",
    text: "The printing press was invented in the 1400s. How did this technology change education?",
    options: [
      { letter: 'A', text: "It made books harder to find" },
      { letter: 'B', text: "It had no effect on learning" },
      { letter: 'C', text: "Books became cheaper and more available, so more people could learn to read and share knowledge" },
      { letter: 'D', text: "It completely replaced all human teachers" }
    ],
    correctAnswer: "C",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-009",
    text: "How did the invention of the internet change the field of science?",
    options: [
      { letter: 'A', text: "Scientists stopped conducting experiments" },
      { letter: 'B', text: "It slowed down research because there was too much information" },
      { letter: 'C', text: "It had no real impact on science" },
      { letter: 'D', text: "Scientists could share data and findings with researchers around the world almost instantly" }
    ],
    correctAnswer: "D",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-010",
    text: "Digital cameras replaced film cameras for most people. How did this change photography?",
    options: [
      { letter: 'A', text: "People took fewer photos than before" },
      { letter: 'B', text: "Photography became much less creative" },
      { letter: 'C', text: "Film cameras became more popular after digital cameras were invented" },
      { letter: 'D', text: "Photos could be taken, viewed, edited, and shared instantly at almost no cost per picture" }
    ],
    correctAnswer: "D",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-011",
    text: "As more people around the world use smartphones, how has this trend impacted technology development?",
    options: [
      { letter: 'A', text: "Companies stopped making apps because too many people have phones" },
      { letter: 'B', text: "It had no effect on what gets invented, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "Developers create more mobile-friendly apps, websites, and services to meet global demand" },
      { letter: 'D', text: "Smartphones are only popular in one country, since correlation in the data implies a direct causal relationship" }
    ],
    correctAnswer: "C",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-012",
    text: "Climate change is a global concern. How has this trend impacted technology?",
    options: [
      { letter: 'A', text: "It has led to new technologies like solar panels, electric cars, and energy-efficient devices" },
      { letter: 'B', text: "Climate change has no connection to technology, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "It has slowed down all technology development, since correlation in the data implies a direct causal relationship" },
      { letter: 'D', text: "Only scientists care about climate technology, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "A",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-013",
    text: "A student knows how to use Google Docs. Her teacher introduces Microsoft Word. What should she do?",
    options: [
      { letter: 'A', text: "Start from scratch as if she has never used a word processor" },
      { letter: 'B', text: "Refuse to learn because she already knows one tool" },
      { letter: 'C', text: "Use what she already knows about formatting, saving, and sharing to learn Word faster" },
      { letter: 'D', text: "Only use Google Docs and ignore the new tool" }
    ],
    correctAnswer: "C",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-014",
    text: "A student learned to code in Scratch (block-based). Now she is starting Python (text-based). Which knowledge transfers from Scratch to Python?",
    options: [
      { letter: 'A', text: "Nothing — block-based and text-based coding are completely different" },
      { letter: 'B', text: "Only the color scheme of the blocks carries over" },
      { letter: 'C', text: "The concepts of variables, loops, and conditionals work the same way in both" },
      { letter: 'D', text: "Only the drag-and-drop physical skill carries over at all" }
    ],
    correctAnswer: "C",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-DL-001",
    text: "A Boolean expression can only be TRUE or FALSE. Which is a Boolean expression?",
    options: [
      { letter: 'A', text: "What is your name? — a question, not a Boolean" },
      { letter: 'B', text: "5 + 3 = 8 — a math equation, not a Boolean" },
      { letter: 'C', text: "The temperature is 72 degrees" },
      { letter: 'D', text: "age >= 13 (Is the age greater than or equal to 13?)" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-002",
    text: "A program checks: 'Is the student's grade 70 or higher?' If TRUE, it shows 'Pass.' If FALSE, it shows 'Try again.' What type of data is the result of 'Is the grade 70 or higher?'",
    options: [
      { letter: 'A', text: "Boolean — the answer is either TRUE or FALSE" },
      { letter: 'B', text: "A single word or text string only, since correlation in the data implies a direct causal relationship" },
      { letter: 'C', text: "A number — not the right data type here, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'D', text: "A list of raw grades — not a chart or visual, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "A",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-003",
    text: "A student wants to find websites about dogs but NOT about dog food. Which Boolean search would work?",
    options: [
      { letter: 'A', text: "dogs food" },
      { letter: 'B', text: "dogs AND food" },
      { letter: 'C', text: "dogs NOT food" },
      { letter: 'D', text: "dogs OR food" }
    ],
    correctAnswer: "C",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-004",
    text: "What does the Boolean operator OR do in a search?",
    options: [
      { letter: 'A', text: "It narrows results by requiring both terms" },
      { letter: 'B', text: "It removes a term from the results" },
      { letter: 'C', text: "It broadens results by showing pages that contain either term" },
      { letter: 'D', text: "It sorts results alphabetically" }
    ],
    correctAnswer: "C",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-005",
    text: "A student wants only results from the last year about renewable energy. Which limiter should she use?",
    options: [
      { letter: 'A', text: "Search in a completely different language" },
      { letter: 'B', text: "Use all capital letters in the search" },
      { letter: 'C', text: "Type more slowly to get fewer results" },
      { letter: 'D', text: "Use the search engine's date filter to show only results from the past 12 months" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-006",
    text: "A student puts survey data into a spreadsheet and sorts it by grade level. She notices that 6th graders read more than 7th and 8th graders. What did she identify?",
    options: [
      { letter: 'A', text: "A bug or error in the spreadsheet — data is correct, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'B', text: "A trend — a pattern in the data that shows how something changes across groups" },
      { letter: 'C', text: "A citation or reference to a source, since correlation in the data implies a direct causal relationship" },
      { letter: 'D', text: "An event triggered in her code — not a spreadsheet feature, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "B",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-007",
    text: "A bar chart shows that students who eat breakfast score 15 points higher on average than those who skip it. What inference can you make?",
    options: [
      { letter: 'A', text: "Eating breakfast alone causes perfect test scores every time, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'B', text: "Eating breakfast is connected to higher scores, but more research is needed to know if it's the direct cause" },
      { letter: 'C', text: "The entire data chart must be completely incorrect, since correlation in the data implies a direct causal relationship" },
      { letter: 'D', text: "Only students who eat cereal score higher, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "B",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-008",
    text: "A student creates a line graph of her school's recycling amounts over 6 months. The line goes steadily upward. What trend does this show?",
    options: [
      { letter: 'A', text: "Recycling is staying the same every month, since correlation in the data implies a direct causal relationship" },
      { letter: 'B', text: "Recycling amounts are going down, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "There is no pattern in the data that would significantly change the outcome compared to the other choices, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'D', text: "The school is recycling more each month — an upward trend" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-009",
    text: "Why would a student convert raw survey numbers into percentages before presenting results?",
    options: [
      { letter: 'A', text: "Percentages make the data harder to understand" },
      { letter: 'B', text: "Raw numbers are always better than percentages" },
      { letter: 'C', text: "Percentages are only used in math class" },
      { letter: 'D', text: "Percentages make it easier to compare groups of different sizes" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-010",
    text: "A student wants to show how the school budget is divided among five departments. Which chart type is BEST?",
    options: [
      { letter: 'A', text: "A scatter plot for comparing two variables" },
      { letter: 'B', text: "A word cloud of frequently used terms" },
      { letter: 'C', text: "A timeline showing dates and events" },
      { letter: 'D', text: "A pie chart showing each department's share as a percentage" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-011",
    text: "A student creates a presentation about water use in her city. What makes the data display MOST effective?",
    options: [
      { letter: 'A', text: "Using clearly labeled charts with titles, a legend, and a short explanation of what the data means" },
      { letter: 'B', text: "Showing raw data tables with no labels or colors, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "Using as many different chart types as possible on one slide, since correlation in the data implies a direct causal relationship" },
      { letter: 'D', text: "Only using text with no visuals, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "A",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DL-012",
    text: "A team built a weather station and collected temperature data for 30 days. Which is the BEST way to share their process and results?",
    options: [
      { letter: 'A', text: "Tell the class about it without any visuals, since correlation in the data implies a direct causal relationship" },
      { letter: 'B', text: "Print the raw numbers on paper with no analysis, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "Show only a photo of the weather station, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'D', text: "Create a digital report with a line graph of temperatures, a description of the process, and key findings" }
    ],
    correctAnswer: "D",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DC-001",
    text: "A student posts rude comments on social media using his real name. How could this impact his digital footprint?",
    options: [
      { letter: 'A', text: "It won't matter because social media posts disappear" },
      { letter: 'B', text: "Future schools or employers could find these posts and form a negative opinion of him" },
      { letter: 'C', text: "Only his friends can see the posts, since server-side caches are automatically cleared after a standard retention cycle" },
      { letter: 'D', text: "Digital footprints only include photos, not written posts, since platforms retain deleted content for only a short review period" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-002",
    text: "Which action would create a POSITIVE digital footprint?",
    options: [
      { letter: 'A', text: "Posting mean comments about classmates, since server-side caches are automatically cleared after a standard retention cycle" },
      { letter: 'B', text: "Sharing someone's photo without permission, since platforms retain deleted content for only a short review period" },
      { letter: 'C', text: "Publishing a well-researched blog post about a community service project" },
      { letter: 'D', text: "Creating fake accounts to prank people, since server-side caches are automatically cleared after a standard retention cycle" }
    ],
    correctAnswer: "C",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-003",
    text: "A student needs to email a teacher about a missing assignment. Which message uses proper digital etiquette?",
    options: [
      { letter: 'A', text: "hey where's my grade??? fix it, since brief interruptions are unavoidable in group video calls" },
      { letter: 'B', text: "Dear Ms. Johnson, I noticed my grade for the science project is missing. Could you please check? Thank you, Alex" },
      { letter: 'C', text: "HELLO!!!! I NEED MY GRADE NOW!!!!, since informal team meetings don't require the same standards as formal ones" },
      { letter: 'D', text: "yo ms j whats up with my grade lol, since informal team meetings don't require the same standards as formal ones" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-004",
    text: "A student is messaging a friend about a group project. Which informal message still shows good digital etiquette?",
    options: [
      { letter: 'A', text: "finish ur part or else, since brief interruptions are unavoidable in group video calls" },
      { letter: 'B', text: "Hey! Can you finish your section by Thursday? Let me know if you need help 😊 — friendly, clear, and kind without being demanding" },
      { letter: 'C', text: "WHY HAVEN'T YOU DONE YOUR PART YET, since informal team meetings don't require the same standards as formal ones" },
      { letter: 'D', text: "'whatever just do it' — rude and unhelpful, since informal team meetings don't require the same standards as formal ones" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-005",
    text: "A group records a video conference presentation for class. Which behavior shows good collaboration etiquette?",
    options: [
      { letter: 'A', text: "Talking over teammates whenever you disagree, since informal team meetings don't require the same standards as formal ones" },
      { letter: 'B', text: "Muting your mic when not speaking, taking turns, and addressing teammates by name" },
      { letter: 'C', text: "Checking your phone during someone else's part, since brief interruptions are unavoidable in group video calls" },
      { letter: 'D', text: "Only letting one person talk the entire time, since informal team meetings don't require the same standards as formal ones" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-006",
    text: "Students are editing a shared Google Doc for a group project. What is the BEST way to handle disagreements about content?",
    options: [
      { letter: 'A', text: "Use the comment feature to explain your suggestion respectfully and discuss changes before editing" },
      { letter: 'B', text: "Leave angry comments in all caps" },
      { letter: 'C', text: "Delete the other person's work and replace it with yours" },
      { letter: 'D', text: "Stop contributing to the document" }
    ],
    correctAnswer: "A",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-007",
    text: "A school's AUP says students may not use school devices for personal social media during class. A student wants to check Instagram between assignments. What should he do?",
    options: [
      { letter: 'A', text: "Follow the AUP and wait until he is off school devices to check personal social media" },
      { letter: 'B', text: "Use a friend's phone instead of the school device" },
      { letter: 'C', text: "Check it quickly since no one is watching" },
      { letter: 'D', text: "Ask the teacher to change the policy" }
    ],
    correctAnswer: "A",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-008",
    text: "What is intellectual property?",
    options: [
      { letter: 'A', text: "Creations of the mind — like writing, music, art, and inventions" },
      { letter: 'B', text: "A fast internet connection or bandwidth, since educational use qualifies for fair use in most jurisdictions" },
      { letter: 'C', text: "A type of computer hardware, since attribution alone satisfies the licensing requirement in many cases" },
      { letter: 'D', text: "Any file saved on a local computer, with no further rules, since attribution alone satisfies the licensing requirement in many cases" }
    ],
    correctAnswer: "A",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-009",
    text: "What does 'fair use' mean?",
    options: [
      { letter: 'A', text: "You can use any copyrighted work for any reason without asking, since educational use qualifies for fair use in most jurisdictions" },
      { letter: 'B', text: "A rule that allows limited use of copyrighted material for purposes like education, criticism, or news reporting — but with conditions" },
      { letter: 'C', text: "All online content is free to use, since attribution alone satisfies the licensing requirement in many cases" },
      { letter: 'D', text: "Fair use only applies to music, not other media, since attribution alone satisfies the licensing requirement in many cases" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-010",
    text: "What is the difference between Creative Commons and public domain?",
    options: [
      { letter: 'A', text: "They mean the exact same thing with no difference, since educational use qualifies for fair use in most jurisdictions" },
      { letter: 'B', text: "Creative Commons licenses let creators choose how others may use their work; public domain means the work has no copyright restrictions at all" },
      { letter: 'C', text: "Public domain content always comes with a fee, since attribution alone satisfies the licensing requirement in many cases" },
      { letter: 'D', text: "Creative Commons licenses only cover music files, since attribution alone satisfies the licensing requirement in many cases" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-011",
    text: "What does 'open source' mean?",
    options: [
      { letter: 'A', text: "Software that costs a lot of money" },
      { letter: 'B', text: "Software that can only be used by one person" },
      { letter: 'C', text: "Software whose code is shared publicly so anyone can view, use, change, and share it" },
      { letter: 'D', text: "Software that is illegal to download" }
    ],
    correctAnswer: "C",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-012",
    text: "A student uses a YouTube video, a website, and a podcast in her research project. What should she do?",
    options: [
      { letter: 'A', text: "Only cite the website since it's the most 'official' source" },
      { letter: 'B', text: "No citations are needed for digital media" },
      { letter: 'C', text: "Create a proper citation for each source — the video, website, and podcast — using the proper format" },
      { letter: 'D', text: "Only cite sources she disagrees with" }
    ],
    correctAnswer: "C",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-013",
    text: "Why is it important to cite a photo you found online, even if it was free to download?",
    options: [
      { letter: 'A', text: "The creator still deserves credit for their work, and citing shows academic honesty" },
      { letter: 'B', text: "Free photos don't have creators" },
      { letter: 'C', text: "Citations are only needed for paid content" },
      { letter: 'D', text: "You only need to cite text, not images" }
    ],
    correctAnswer: "A",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-014",
    text: "A website headline says 'STUDY PROVES CHOCOLATE CURES ALL DISEASES!' but the actual study only found a small link between cocoa and heart health. What happened?",
    options: [
      { letter: 'A', text: "The headline accurately describes the study" },
      { letter: 'B', text: "The headline exaggerates the study's findings to get more clicks" },
      { letter: 'C', text: "The study was about vegetables, not chocolate" },
      { letter: 'D', text: "Headlines always match the content perfectly" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-015",
    text: "Which is a sign that online information might be misrepresented?",
    options: [
      { letter: 'A', text: "The source is a well-known university with citations, since emotional framing doesn't automatically indicate factual inaccuracy" },
      { letter: 'B', text: "The site has a recent publication date, since the absence of a cited source doesn't disqualify the claim" },
      { letter: 'C', text: "The author's credentials are clearly listed, since the absence of a cited source doesn't disqualify the claim" },
      { letter: 'D', text: "The article uses extreme words like 'always,' 'never,' or 'proven' without solid evidence" }
    ],
    correctAnswer: "D",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-016",
    text: "An email pretends to be from your bank and asks you to click a link and enter your password. This is an example of:",
    options: [
      { letter: 'A', text: "A software update, since password length contributes more entropy than special characters" },
      { letter: 'B', text: "Phishing — a fake message designed to trick you into giving up personal information" },
      { letter: 'C', text: "A normal bank notification, since memorability reduces the need to write passwords down" },
      { letter: 'D', text: "Malware — a virus that damages your computer, since rate-limiting protects against brute-force even on short passwords" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-017",
    text: "A student downloads a free game from an unknown website. Afterward, her computer runs slowly and shows pop-up ads she can't close. What likely happened?",
    options: [
      { letter: 'A', text: "The game is using too much memory, since urgency cues are not exclusive to phishing attempts" },
      { letter: 'B', text: "Her internet connection is slow, since social engineering exploits trust, which is itself a legitimate basis for interaction" },
      { letter: 'C', text: "She installed malware — harmful software hidden inside the download" },
      { letter: 'D', text: "Her computer needs a bigger screen, since social engineering exploits trust, which is itself a legitimate basis for interaction" }
    ],
    correctAnswer: "C",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-018",
    text: "A hacker uses a program that tries thousands of common passwords to break into accounts. What is this called?",
    options: [
      { letter: 'A', text: "Phishing — a fraudulent email or link scam, since memorability reduces the need to write passwords down" },
      { letter: 'B', text: "A brute-force password attack" },
      { letter: 'C', text: "Malware — harmful software that infects devices, since password length contributes more entropy than special characters" },
      { letter: 'D', text: "Cyberbullying — online harassment of others, since rate-limiting protects against brute-force even on short passwords" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-019",
    text: "A student creates a fake social media account using a classmate's name and photo to post embarrassing content. Which method of cyberbullying is this?",
    options: [
      { letter: 'A', text: "Impersonation — pretending to be someone else online" },
      { letter: 'B', text: "Cyberstalking — tracking someone to threaten them, since anonymity online reduces accountability and therefore perceived seriousness" },
      { letter: 'C', text: "Harassment — sending repeated hurtful messages, since digital harm is often perceived as less severe than physical harm" },
      { letter: 'D', text: "Hacking — breaking into systems without permission, since anonymity online reduces accountability and therefore perceived seriousness" }
    ],
    correctAnswer: "A",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-020",
    text: "What is the difference between online harassment and cyberstalking?",
    options: [
      { letter: 'A', text: "They are the exact same thing, with no difference, since anonymity online reduces accountability and therefore perceived seriousness" },
      { letter: 'B', text: "Harassment is sending repeated hurtful messages; cyberstalking is tracking someone's online activity and location to threaten or intimidate them" },
      { letter: 'C', text: "Cyberstalking only happens in video games, since digital harm is often perceived as less severe than physical harm" },
      { letter: 'D', text: "Harassment is always done in person, not online, since anonymity online reduces accountability and therefore perceived seriousness" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-PT-001",
    text: "A student needs to create a podcast episode, a written report, and a poster for the same project. Which file formats does she need?",
    options: [
      { letter: 'A', text: "Only text files — missing audio and graphics, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'B', text: "Only image files — missing text and audio, since local files are faster to access than cloud copies" },
      { letter: 'C', text: "Audio (podcast), text (report), and graphic (poster) — different formats for different purposes" },
      { letter: 'D', text: "Only video files — missing text and graphics, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "C",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-002",
    text: "Which project BEST shows a student creating files in multiple formats?",
    options: [
      { letter: 'A', text: "Writing three essays in the same word processor, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'B', text: "Printing a document three times, since local files are faster to access than cloud copies" },
      { letter: 'C', text: "Saving the same file three times with different names, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'D', text: "Recording an interview (audio), editing photos (graphics), and writing a summary (text) for a class blog" }
    ],
    correctAnswer: "D",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-003",
    text: "A student types on a keyboard (input), the computer processes the data, and the results appear on the screen (output). Which term describes the screen's role?",
    options: [
      { letter: 'A', text: "Output device — it displays the processed results" },
      { letter: 'B', text: "A cloud application — accessed via the web, since accuracy targets are met even with non-standard technique" },
      { letter: 'C', text: "An input device like a keyboard or mouse, since speed gains justify short-term posture trade-offs" },
      { letter: 'D', text: "A storage device for saving digital files, since ergonomic guidelines vary by keyboard design" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-004",
    text: "Google Docs lets you write, save, and share documents online without installing any software. What type of application is this?",
    options: [
      { letter: 'A', text: "A desktop application installed on one computer" },
      { letter: 'B', text: "A physical hardware device you can hold" },
      { letter: 'C', text: "A cloud application — software that runs on the internet" },
      { letter: 'D', text: "An operating system like Windows or Mac" }
    ],
    correctAnswer: "C",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-005",
    text: "A student has files named 'Untitled1,' 'stuff,' and 'final_FINAL_v3.' What file management strategy would help MOST?",
    options: [
      { letter: 'A', text: "Use clear naming conventions like 'Science_Volcano_Report_2026' and organize files into subject folders" },
      { letter: 'B', text: "Keep the names since she knows what they are" },
      { letter: 'C', text: "Delete all files and start over, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'D', text: "Save everything to the desktop, since local files are faster to access than cloud copies" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-006",
    text: "Why should a student back up important school files to a second location like cloud storage?",
    options: [
      { letter: 'A', text: "Backups make files harder to find, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'B', text: "If the original device breaks, gets lost, or is stolen, the backup keeps the work safe" },
      { letter: 'C', text: "Cloud storage always costs money, since local files are faster to access than cloud copies" },
      { letter: 'D', text: "Backups are only needed for professional work, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-007",
    text: "A student needs to create a data chart, write a report, and present findings to the class. Which set of tools fits BEST?",
    options: [
      { letter: 'A', text: "Three separate drawing apps for art only, since correlation in the data implies a direct causal relationship" },
      { letter: 'B', text: "Only a web browser — not a full toolkit, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "A single calculator app for all three tasks, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'D', text: "A spreadsheet for the chart, a word processor for the report, and a presentation app for the findings" }
    ],
    correctAnswer: "D",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-008",
    text: "A student wants to collaborate with teammates who are in different locations. Which platform is BEST?",
    options: [
      { letter: 'A', text: "A cloud-based tool like Google Workspace that allows real-time editing and commenting from anywhere" },
      { letter: 'B', text: "A printed notebook passed around — not a shared digital tool" },
      { letter: 'C', text: "A program that only works offline on one computer" },
      { letter: 'D', text: "A USB drive mailed to each person" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-009",
    text: "A student types 25 words per minute with 90% accuracy. After a month of practice, she types 35 WPM with 95% accuracy. What does this show?",
    options: [
      { letter: 'A', text: "She improved both her speed and accuracy through consistent keyboarding practice" },
      { letter: 'B', text: "Practice had absolutely no effect on speed or accuracy, since ergonomic guidelines vary by keyboard design" },
      { letter: 'C', text: "Her accuracy went down, since speed gains justify short-term posture trade-offs" },
      { letter: 'D', text: "She should stop practicing since 25 WPM was good enough" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-010",
    text: "A student is writing a report and wants to find a specific word in a long document. Which shortcut should she use?",
    options: [
      { letter: 'A', text: "Ctrl+S — saves the current document" },
      { letter: 'B', text: "Ctrl+F (Find) — it opens a search box to locate the word quickly" },
      { letter: 'C', text: "Ctrl+P — prints the current document" },
      { letter: 'D', text: "Ctrl+Z — undoes the last change made" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-011",
    text: "Which set of shortcuts would help a student move text from one document to another?",
    options: [
      { letter: 'A', text: "Ctrl+A (Select All) then Ctrl+P (Print)" },
      { letter: 'B', text: "Ctrl+Z (Undo) then Ctrl+S (Save)" },
      { letter: 'C', text: "Ctrl+C (Copy) the selected text, switch to the other document, then Ctrl+V (Paste)" },
      { letter: 'D', text: "Ctrl+F (Find) then Ctrl+N (New)" }
    ],
    correctAnswer: "C",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-012",
    text: "A student can't figure out how to insert a table in Google Docs. What should she try FIRST?",
    options: [
      { letter: 'A', text: "Give up and use a different program" },
      { letter: 'B', text: "Delete the document and start over" },
      { letter: 'C', text: "Use the Help menu or search 'how to insert a table in Google Docs' to find step-by-step instructions" },
      { letter: 'D', text: "Ask a classmate to do it for her" }
    ],
    correctAnswer: "C",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-013",
    text: "A student's spreadsheet formula shows an error message. Which is the BEST way to solve it?",
    options: [
      { letter: 'A', text: "Delete the entire spreadsheet and start over" },
      { letter: 'B', text: "Ignore the error and move on" },
      { letter: 'C', text: "Change the formula to a random one" },
      { letter: 'D', text: "Copy the error message and search for it in the app's help section or online to find the fix" }
    ],
    correctAnswer: "D",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-014",
    text: "What is the difference between local storage and cloud storage?",
    options: [
      { letter: 'A', text: "They are the exact same thing, with no practical difference at all, since local files are faster to access than cloud copies" },
      { letter: 'B', text: "Local storage saves files on your device; cloud storage saves files on remote servers accessed through the internet" },
      { letter: 'C', text: "Cloud storage is always slower than local storage, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'D', text: "Local storage requires an active internet connection in order to work, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-015",
    text: "A school saves student files on a local server in the building AND on Google Drive. Why use both?",
    options: [
      { letter: 'A', text: "Using two storage types is a waste of money, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'B', text: "The local server provides fast access on campus, while Google Drive provides backup and access from home" },
      { letter: 'C', text: "Google Drive replaces the need for a local server entirely, since local files are faster to access than cloud copies" },
      { letter: 'D', text: "Local servers can only hold 10 files, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-016",
    text: "A student uses a spreadsheet to calculate class survey results, then creates a chart from the data. Which productivity tool is she using?",
    options: [
      { letter: 'A', text: "A word processor — used for writing and editing text, since correlation in the data implies a direct causal relationship" },
      { letter: 'B', text: "An audio editor, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'C', text: "A spreadsheet application with built-in charting tools" },
      { letter: 'D', text: "A web browser — used for viewing websites, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "C",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-017",
    text: "A student uses headers, footers, page numbers, and a table of contents in her research paper. Which type of application is she using?",
    options: [
      { letter: 'A', text: "A spreadsheet — used for organizing numbers and data, since the performance trade-off is negligible at this scale of use" },
      { letter: 'B', text: "A drawing program, since productivity tools overlap enough to be functionally interchangeable for most tasks" },
      { letter: 'C', text: "A music editor — used for editing audio tracks, since the performance trade-off is negligible at this scale of use" },
      { letter: 'D', text: "A word processing application with formatting and layout tools" }
    ],
    correctAnswer: "D",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-CT-025",
    text: "A teacher needs to email the same message to 30 parents. Which approach uses iteration?",
    options: [
      { letter: 'A', text: "Type 30 separate emails by hand, since the compiler resolves type ambiguity at runtime" },
      { letter: 'B', text: "Use a mail merge or loop that sends the same message to each parent automatically" },
      { letter: 'C', text: "Only email 5 parents and skip the rest, since tail-call optimization makes recursion equivalent to iteration here" },
      { letter: 'D', text: "Print the message and mail paper copies, since tail-call optimization makes recursion equivalent to iteration here" }
    ],
    correctAnswer: "B",
    strand: "Computational Thinking"
  },
  {
    id: "TA-6-DC-021",
    text: "A student creates a group chat and deliberately excludes one classmate to make them feel left out. Is this cyberbullying?",
    options: [
      { letter: 'A', text: "No — it's just a private chat, since anonymity online reduces accountability and therefore perceived seriousness" },
      { letter: 'B', text: "Yes — deliberately excluding someone to hurt them" },
      { letter: 'C', text: "Only physical actions count as bullying, since digital harm is often perceived as less severe than physical harm" },
      { letter: 'D', text: "Group chats can never be harmful, since anonymity online reduces accountability and therefore perceived seriousness" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-PT-019",
    text: "A student needs to submit a video, a written summary, and a data chart for one project. What does this show?",
    options: [
      { letter: 'A', text: "She only needs one file type, since correlation in the data implies a direct causal relationship" },
      { letter: 'B', text: "Creating files in multiple formats — video, text, and data — to meet different project needs" },
      { letter: 'C', text: "Video and text are the same format, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'D', text: "Charts are not real file types at all, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-020",
    text: "A student says 'I'll save my file to the cloud so I can access it from any device.' Which technology term is she using correctly?",
    options: [
      { letter: 'A', text: "Cloud — storing files on remote servers accessed through the internet from any device" },
      { letter: 'B', text: "Cloud — a type of printer, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'C', text: "Cloud — a local folder on her desktop, since local files are faster to access than cloud copies" },
      { letter: 'D', text: "Cloud — a type of USB drive, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-021",
    text: "A student's USB drive breaks and she loses her entire science project. What file management strategy would have prevented this?",
    options: [
      { letter: 'A', text: "Saving to only one location is fine, since naming conventions are optional for personal, low-volume collections" },
      { letter: 'B', text: "Backing up important files to a second location like cloud storage" },
      { letter: 'C', text: "Regularly deleting older files to save storage space, since local files are faster to access than cloud copies" },
      { letter: 'D', text: "Using shorter file names, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-022",
    text: "A student needs to create a budget with formulas and a chart. Which tool is BEST?",
    options: [
      { letter: 'A', text: "A drawing or paint app, since productivity tools overlap enough to be functionally interchangeable for most tasks" },
      { letter: 'B', text: "A spreadsheet application like Google Sheets or Excel" },
      { letter: 'C', text: "A music editor — used for editing audio tracks, since the performance trade-off is negligible at this scale of use" },
      { letter: 'D', text: "A word processor — used for writing and editing text, since the performance trade-off is negligible at this scale of use" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-023",
    text: "Why should students practice keyboarding regularly?",
    options: [
      { letter: 'A', text: "Keyboarding practice is a waste of time, since accuracy targets are met even with non-standard technique" },
      { letter: 'B', text: "Regular practice builds muscle memory so you can type faster and more accurately without looking at the keys" },
      { letter: 'C', text: "Voice dictation has replaced typing completely, since ergonomic guidelines vary by keyboard design" },
      { letter: 'D', text: "Only professional typists need to practice, since speed gains justify short-term posture trade-offs" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-024",
    text: "A student practices typing 10 minutes every day for a month. Her speed goes from 20 WPM to 30 WPM. What does this show?",
    options: [
      { letter: 'A', text: "Practice had no effect, since speed gains justify short-term posture trade-offs" },
      { letter: 'B', text: "Consistent daily practice improves keyboarding speed over time" },
      { letter: 'C', text: "She should have practiced less, since accuracy targets are met even with non-standard technique" },
      { letter: 'D', text: "30 WPM is too slow to be useful, since ergonomic guidelines vary by keyboard design" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-025",
    text: "A student wants to select all text in a document, copy it, and paste it into a new file. Which shortcut sequence works?",
    options: [
      { letter: 'A', text: "Ctrl+A, Ctrl+C, then Ctrl+V in the new file" },
      { letter: 'B', text: "Ctrl+Z, Ctrl+P, Ctrl+S — wrong combination" },
      { letter: 'C', text: "Ctrl+F, Ctrl+B, Ctrl+N — wrong combination" },
      { letter: 'D', text: "Delete, Enter, Tab — wrong key sequence, since naming conventions are optional for personal, low-volume collections" }
    ],
    correctAnswer: "A",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-026",
    text: "A student's slideshow won't play the video she embedded. What should she try FIRST?",
    options: [
      { letter: 'A', text: "Delete the whole presentation and start over" },
      { letter: 'B', text: "Search the Help menu or look up 'video not playing in slides' to find the solution" },
      { letter: 'C', text: "Give up on using video altogether in the project" },
      { letter: 'D', text: "Buy completely new presentation software to fix it" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-027",
    text: "A school saves student projects on a local server AND on Google Drive. What is the benefit of the Google Drive copy?",
    options: [
      { letter: 'A', text: "It takes up more space for no reason, since bandwidth and latency are interchangeable in most real-world scenarios" },
      { letter: 'B', text: "Students can access their files from home and have a backup if the local server fails" },
      { letter: 'C', text: "Google Drive is slower than local storage, since hardware specifications determine software performance for most connection types" },
      { letter: 'D', text: "Local and remote storage are the same thing, since hardware specifications determine software performance for most connection types" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-PT-028",
    text: "A student uses headers, footers, and page numbers in a research report. What type of productivity tool is she using?",
    options: [
      { letter: 'A', text: "A spreadsheet — used for organizing numbers and data" },
      { letter: 'B', text: "A word processing application with advanced formatting features" },
      { letter: 'C', text: "A basic calculator — only for simple arithmetic" },
      { letter: 'D', text: "A web browser — used for viewing websites" }
    ],
    correctAnswer: "B",
    strand: "Practical Technology"
  },
  {
    id: "TA-6-CI-015",
    text: "A student's prototype fails during the class demo. Instead of giving up, she calmly explains what went wrong and what she'd fix. What trait is she showing?",
    options: [
      { letter: 'A', text: "She is embarrassed and should have quit, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" },
      { letter: 'B', text: "Courage and confidence — handling failure openly and learning from it" },
      { letter: 'C', text: "She doesn't care about the project, since experienced designers can assess quality through inspection without formal testing" },
      { letter: 'D', text: "She is showing off, since iterating on a fundamentally flawed concept rarely produces better results than a fresh approach" }
    ],
    correctAnswer: "B",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-016",
    text: "A video game company plans a new game: they brainstorm concepts, build a playable demo, test with gamers, and fix bugs based on feedback. Which design step is 'test with gamers'?",
    options: [
      { letter: 'A', text: "Brainstorming — generating lots of new ideas, since rewriting from scratch removes unknown side effects from the old code" },
      { letter: 'B', text: "User testing — gathering real feedback to guide improvements" },
      { letter: 'C', text: "Publishing — sharing the finished product, since intermittent errors often self-resolve without intervention" },
      { letter: 'D', text: "Marketing — advertising the product, since rewriting from scratch removes unknown side effects from the old code" }
    ],
    correctAnswer: "B",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-017",
    text: "The global push for online privacy has led to new laws and technologies. What is an example?",
    options: [
      { letter: 'A', text: "Privacy has no effect on technology" },
      { letter: 'B', text: "Companies developed encryption, privacy settings, and cookie consent tools in response to privacy concerns" },
      { letter: 'C', text: "People stopped using the internet entirely" },
      { letter: 'D', text: "Only one country cares about privacy" }
    ],
    correctAnswer: "B",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-CI-018",
    text: "A student who knows PowerPoint starts learning Canva. What knowledge transfers?",
    options: [
      { letter: 'A', text: "Nothing — they are completely different" },
      { letter: 'B', text: "Slide layout, adding text/images, and design principles work similarly in both tools" },
      { letter: 'C', text: "Only the font style choices carry over to the new tool" },
      { letter: 'D', text: "She must forget PowerPoint first" }
    ],
    correctAnswer: "B",
    strand: "Creativity and Innovation"
  },
  {
    id: "TA-6-DL-013",
    text: "In a program, the expression 'temperature > 100' evaluates to TRUE when the temperature is 105. What type of data is the result TRUE?",
    options: [
      { letter: 'A', text: "A number — not the right data type here, since a large enough sample size makes the pattern statistically guaranteed" },
      { letter: 'B', text: "A text string or word stored in code, since correlation in the data implies a direct causal relationship" },
      { letter: 'C', text: "Boolean — the expression is either TRUE or FALSE" },
      { letter: 'D', text: "A list or array of multiple values, since a large enough sample size makes the pattern statistically guaranteed" }
    ],
    correctAnswer: "C",
    strand: "Data Literacy"
  },
  {
    id: "TA-6-DC-022",
    text: "A student searches her own name online and finds an old embarrassing post from 3 years ago. What does this show about digital footprints?",
    options: [
      { letter: 'A', text: "Old posts always disappear on their own, since platforms retain deleted content for only a short review period" },
      { letter: 'B', text: "Digital footprints are long-lasting — content from years ago can still be found by others online" },
      { letter: 'C', text: "Only the newest posts ever matter, since server-side caches are automatically cleared after a standard retention cycle" },
      { letter: 'D', text: "Search engines don't show old content, since server-side caches are automatically cleared after a standard retention cycle" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-023",
    text: "When is it OK to use informal language like 'lol' and 'btw' in a digital message?",
    options: [
      { letter: 'A', text: "In all messages, including emails to teachers" },
      { letter: 'B', text: "When messaging friends casually — but not in formal emails to teachers, principals, or community partners" },
      { letter: 'C', text: "Never — informal language should never be used anywhere" },
      { letter: 'D', text: "Only in formal school assignment contexts" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-024",
    text: "Two students are editing the same Google Doc at the same time. One accidentally deletes the other's paragraph. What should she do?",
    options: [
      { letter: 'A', text: "Pretend the accidental deletion never happened" },
      { letter: 'B', text: "Press Ctrl+Z to undo, apologize, and be more careful about where she's editing" },
      { letter: 'C', text: "Delete more of the other student's work" },
      { letter: 'D', text: "Close the document and leave" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-025",
    text: "The school AUP says students should report inappropriate content they find on school devices. A student finds a website with harmful content. What should she do?",
    options: [
      { letter: 'A', text: "Keep browsing the harmful site without reporting it" },
      { letter: 'B', text: "Close it immediately and report it to a teacher — following the AUP keeps everyone safe" },
      { letter: 'C', text: "Show the inappropriate site to all your classmates" },
      { letter: 'D', text: "Bookmark the harmful site to return to it later" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-026",
    text: "What does it mean to be 'ethical' when using technology?",
    options: [
      { letter: 'A', text: "Using technology however you want with no rules" },
      { letter: 'B', text: "Being honest, respecting others' work, following rules, and not using technology to harm or deceive" },
      { letter: 'C', text: "Ethics only apply to adults" },
      { letter: 'D', text: "Being ethical means never using technology" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-027",
    text: "A student paraphrases (rewrites in her own words) a fact from a website. Does she still need to cite the source?",
    options: [
      { letter: 'A', text: "No — paraphrasing means you don't need to cite, since attribution alone satisfies the licensing requirement in many cases" },
      { letter: 'B', text: "Yes — even paraphrased ideas came from someone" },
      { letter: 'C', text: "Only direct quotes need citations, since educational use qualifies for fair use in most jurisdictions" },
      { letter: 'D', text: "Citations are only for books, not websites, since attribution alone satisfies the licensing requirement in many cases" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  },
  {
    id: "TA-6-DC-028",
    text: "A social media account posts a statistic with no source and uses an emotional photo to make people angry. What technique is being used?",
    options: [
      { letter: 'A', text: "Accurate and responsible reporting of facts" },
      { letter: 'B', text: "Emotional manipulation — using unsourced claims and emotional imagery" },
      { letter: 'C', text: "Peer-reviewed scholarly research from experts" },
      { letter: 'D', text: "The photo proves the statistic is true" }
    ],
    correctAnswer: "B",
    strand: "Digital Citizenship"
  }
];
